import { Option, Select } from '@moia-dev/pace-web'
import sortBy from 'lodash/sortBy'
import { useQueryState } from 'nuqs'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useGlobalRegion } from './useSelectedRegion'
import { useUserFleets } from './useUserFleets'

export const SELECTED_FLEET_ID = 'fleet_id'

type UserSettingsFleetDropdownProps = {
  /**
   * Custom error override (ex. when fleet is not accessible due to region mismatch from parent context).
   */
  error?: boolean
}

export const UserSettingsFleetDropdown = ({
  error: errorOverride,
}: UserSettingsFleetDropdownProps = {}) => {
  const { t } = useTranslation()
  const [globalRegion] = useGlobalRegion()

  const isInitialMount = useRef(true)

  const globalRegionId = globalRegion?.id

  const fleets = useUserFleets({
    filterFleetClaims: true,
    regionId: globalRegionId,
    skip: !globalRegion,
  })

  const [selectedFleetId, setSelectedFleetId] = useQueryState(
    SELECTED_FLEET_ID,
    {
      defaultValue: '',
      clearOnDefault: true,
    },
  )

  const fleetOptions = sortBy(
    fleets.map(fleet => ({
      value: fleet.id,
      label: fleet.name,
    })),
    fleet => fleet.label,
  )

  const fleetOptionsMap = Object.fromEntries(
    fleetOptions.map(opt => [opt.value, opt.label]),
  )

  const selectedFleet = fleetOptions.find(opt => opt.value === selectedFleetId)
  const hasFleetError =
    errorOverride !== undefined
      ? errorOverride
      : !selectedFleet && fleetOptions.length > 0

  // Reset the selectedFleetId only if it's not available in the current region
  // biome-ignore lint/correctness/useExhaustiveDependencies: We need to reset the selectedFleetId when the region changes
  useEffect(() => {
    // skip this on initial render so we do not reset the fleet when navigating between pages
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // If fleets is empty, assume data is still loading and don't reset.
    // TBD: Not sure if we need to handle the 0 fleets case at some point
    const hasFleetDataLoaded = fleets.length > 0

    // Only reset if we have fleet data loaded and the selected fleet is not in the available fleets (not in region)
    const isFleetInRegion = fleets.some(fleet => fleet.id === selectedFleetId)

    // Skip auto-clear when the parent is handling the error (e.g. fleet-tracing
    // permission check) — clearing the fleet_id would remove the signal the
    // parent needs to show the permission error.
    if (
      selectedFleetId &&
      hasFleetDataLoaded &&
      !isFleetInRegion &&
      !errorOverride
    ) {
      setSelectedFleetId('')
    }
  }, [globalRegionId, selectedFleetId, fleets])

  return (
    <Select<string>
      css={theme => ({
        minWidth: '200px',
        padding: `${theme.space.Space2} ${theme.space.Space3}`,
      })}
      renderValue={value => fleetOptionsMap[value]}
      placeholder={t('common_fleet_label')}
      value={selectedFleetId || ''}
      onChange={value => {
        setSelectedFleetId(value || null)
      }}
      error={hasFleetError}
      options={fleetOptions.map(option => (
        <Option key={option.value} value={option.value}>
          {option.label}
        </Option>
      ))}
    />
  )
}
